import { useEffect } from 'react'
import { motion } from 'framer-motion'

const PHONE = '+1 (786) 296-1439'
const PHONE_HREF = 'tel:+17862961439'

const LandingPage = ({ image, alt, formUrl, title, headline, subtitle, features, cta }) => {
  useEffect(() => {
    document.title = `${title} | JD Rivero`
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [title])

  return (
    <div className="min-h-screen bg-ink font-body flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>

      {/* ── Split: Image | Text ── */}
      <div className="flex-1 flex flex-col lg:flex-row lg:min-h-screen">

        {/* Left — Cover image */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="relative w-full lg:w-1/2 aspect-[4/5] sm:aspect-[3/4] lg:aspect-auto lg:min-h-screen p-4 sm:p-6 lg:p-0"
        >
          <img
            src={image}
            alt={alt}
            className="w-full h-full object-cover rounded-2xl lg:rounded-none"
          />
        </motion.div>

        {/* Right — Text content */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-20 pt-10 pb-12 sm:pt-12 sm:pb-16 lg:py-20">
          {/* Logo */}
          <motion.a
            href="https://www.jdrivero.com"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-8 sm:mb-10"
          >
            <img src="/logo.webp" alt="JD Rivero" className="h-9 sm:h-10" />
          </motion.a>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
            className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[0.9] tracking-tighter uppercase text-white mb-5 sm:mb-6"
          >
            {headline}
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut', delay: 0.5 }}
            className="text-base sm:text-lg font-medium leading-relaxed text-white/80 mb-7 sm:mb-8 max-w-lg"
          >
            {subtitle}
          </motion.p>

          {/* Feature pills */}
          {features && features.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex flex-wrap gap-2 sm:gap-2.5 mb-8 sm:mb-10"
            >
              {features.map((f, i) => (
                <span
                  key={i}
                  className="bg-white/10 border border-white/15 text-white text-xs sm:text-sm font-semibold px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-full"
                >
                  {f}
                </span>
              ))}
            </motion.div>
          )}

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="flex flex-col sm:flex-row items-start sm:items-center gap-4"
          >
            <a
              href={formUrl + (window.location.search ? (formUrl.includes('?') ? '&' : '?') + window.location.search.slice(1) : '')}
              className="inline-block bg-primary hover:bg-yellow-400 text-black font-bold text-sm tracking-widest uppercase px-8 sm:px-10 py-4 rounded-lg shadow-lg transition-colors"
            >
              {cta || 'Get a Free Quote'}
            </a>

            <a
              href={PHONE_HREF}
              className="text-white/60 hover:text-primary text-lg font-medium tracking-wide transition-colors"
            >
              {PHONE}
            </a>
          </motion.div>
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="w-full bg-primary py-3 px-6 flex items-center justify-center" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <p className="text-black font-bold text-sm sm:text-base tracking-wider uppercase text-center">
          Licensed &amp; Insured — Hablamos Español
        </p>
      </div>
    </div>
  )
}

export default LandingPage
